import React, { useRef, useState } from "react";
import toast from "react-hot-toast";
import CrisisNotice from "./CrisisNotice";
import { sendContactForm, CONTACT_MESSAGE_MAX_LENGTH } from "../services/api/contactService";
import {
  buildInquiryContact,
  emptyInquiryValues,
  firstInvalidAnswer,
  missingRequired,
} from "../data/inquiryForms";

/**
 * Structured inquiry form (organizations, schools, request a call). Answers
 * are composed into the existing POST /api/public/contact `message`; API
 * errors (validation, rate limit) are shown the way the Home contact form
 * shows them, plus an inline alert so they stay on screen.
 *
 * @param {object} props
 * @param {object} props.form - Definition from data/inquiryForms.js
 * @param {2|3} [props.headingLevel] - Level of the form's heading, default 2
 * @param {boolean} [props.showHeading] - false when a surrounding heading
 *   already names the form (the form is then labelled with aria-label)
 */
export default function InquiryForm({ form, headingLevel = 2, showHeading = true }) {
  const [values, setValues] = useState(() => emptyInquiryValues(form));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  // Guards a double click landing before `loading` re-renders the button.
  const inFlight = useRef(false);

  const fieldId = (name, option) =>
    `${form.id}-${name}${option ? `-${option.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : ""}`;

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setValues((prev) => {
      if (type === "checkbox") {
        const current = prev[name] || [];
        return { ...prev, [name]: checked ? [...current, value] : current.filter((v) => v !== value) };
      }
      return { ...prev, [name]: value };
    });
  };

  const fail = (message) => {
    setError(message);
    toast.error(message);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    setSent(false);

    const missing = missingRequired(form, values);
    if (missing.length) {
      fail(`Please fill in: ${missing.join(", ")}.`);
      return;
    }
    const invalid = firstInvalidAnswer(form, values);
    if (invalid) {
      fail(invalid);
      return;
    }
    const contact = buildInquiryContact(form, values);
    if (contact.message.length > CONTACT_MESSAGE_MAX_LENGTH) {
      fail(`Please shorten your answers (at most ${CONTACT_MESSAGE_MAX_LENGTH} characters in total).`);
      return;
    }

    setError("");
    setLoading(true);
    inFlight.current = true;
    try {
      await sendContactForm(contact);
      toast.success(form.successToast || "Thanks! I'll be in touch shortly.");
      setSent(true);
      setValues(emptyInquiryValues(form));
    } catch (err) {
      fail(err.message || "Failed to send your inquiry. Please try again.");
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  const renderField = (f) => {
    const id = fieldId(f.name);

    if (f.type === "radio" || f.type === "checkbox") {
      return (
        <fieldset key={f.name} className="inquiry-choices" disabled={loading}>
          <legend>{f.label}</legend>
          {f.options.map((option) => {
            const optionId = fieldId(f.name, option);
            const checked =
              f.type === "checkbox" ? values[f.name].includes(option) : values[f.name] === option;
            return (
              <label key={option} htmlFor={optionId} className="inquiry-option">
                <input
                  id={optionId}
                  type={f.type}
                  name={f.name}
                  value={option}
                  checked={checked}
                  onChange={onChange}
                />
                {option}
              </label>
            );
          })}
        </fieldset>
      );
    }

    const hintId = f.hint ? `${id}-hint` : undefined;
    const common = {
      id,
      name: f.name,
      value: values[f.name],
      onChange,
      required: f.required || undefined,
      maxLength: f.maxLength,
      autoComplete: f.autoComplete,
      placeholder: f.placeholder,
      "aria-describedby": hintId,
      disabled: loading,
    };

    return (
      <React.Fragment key={f.name}>
        <label htmlFor={id}>
          {f.label}
          {f.required ? <span aria-hidden="true"> *</span> : null}
          {f.type === "textarea" ? (
            <textarea {...common} />
          ) : (
            <input {...common} type={f.type} min={f.min} inputMode={f.type === "number" ? "numeric" : undefined} />
          )}
        </label>
        {f.hint ? (
          <p id={hintId} className="field-hint">
            {f.hint}
          </p>
        ) : null}
      </React.Fragment>
    );
  };

  const Heading = `h${headingLevel}`;
  return (
    <form
      className="inquiry-form"
      onSubmit={onSubmit}
      aria-labelledby={showHeading ? `${form.id}-heading` : undefined}
      aria-label={showHeading ? undefined : form.heading}
    >
      {showHeading ? <Heading id={`${form.id}-heading`}>{form.heading}</Heading> : null}
      {form.intro ? <p className="inquiry-intro">{form.intro}</p> : null}
      <p className="field-hint inquiry-required-note">Fields marked * are required.</p>

      {form.fields.map(renderField)}

      {/* Honeypot: visually hidden and skipped by keyboard/screen readers */}
      <div className="hp-field" aria-hidden="true">
        <label>
          Website
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={values.website}
            onChange={onChange}
          />
        </label>
      </div>

      {error ? (
        <p className="inquiry-error" role="alert">
          {error}
        </p>
      ) : null}
      {/* Always rendered so screen readers announce the change */}
      <p className={sent ? "inquiry-success" : "sr-only"} role="status">
        {sent ? form.successText || "Thanks! Your inquiry was sent. I'll be in touch shortly." : ""}
      </p>

      <button className="btn-cta" type="submit" disabled={loading}>
        {loading ? "Sending..." : form.submitLabel || "Send Inquiry"}
      </button>
      <CrisisNotice variant="form" />
    </form>
  );
}
